/**
 * DairePage Component - By John Bellew - Infinia.ie - last updated 10/07/2025
 * 
 * A comprehensive AI chat interface page with advanced features:
 * - Full-page Daire AI chat integration
 * - Dynamic sidebar with related questions and topics
 * - Pinned questions functionality for important queries
 * - Chat history and analytics tracking
 * - Multi-tab content organization (documents, news, analytics)
 * - Content filtering and search capabilities
 * - Help modal integration
 * - Responsive design for various screen sizes
 * - Real-time question and topic suggestions
 * 
 * Features:
 * - Interactive question clicking and navigation
 * - Pin/unpin question management
 * - Dynamic content loading based on chat activity
 * - Sidebar visibility based on user interaction
 * - Tab-based content switching
 * - Keyboard navigation support
 * 
 * @returns {JSX.Element} DairePage component with full AI chat interface
 */
import React, { useState, useRef, useEffect } from 'react';
import DaireChat from '../components/Daire/DaireChat';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faThumbtack
} from '@fortawesome/free-solid-svg-icons';
import styles from './DairePage.module.css';
import InfoModal from '../modals/InfoModal';
import apiHelper from '../utils/apiHelper';

/**
 * DairePage component for full-page AI chat interface
 * with sidebar navigation and dynamic content management
 */
function DairePage() {
  const [activeTab, setActiveTab] = useState('chat');
  const [showContent, setShowContent] = useState(false);
  const [contentType, setContentType] = useState('all');
  const [showHelp, setShowHelp] = useState(false);
  const [relatedQuestions, setRelatedQuestions] = useState([]);
  const [topics, setTopics] = useState([]);
  const [isUpdatingSummary, setIsUpdatingSummary] = useState(false);
  const [isUpdatingTopics, setIsUpdatingTopics] = useState(false);
  const [pinnedQuestions, setPinnedQuestions] = useState([]);
  const [messages, setMessages] = useState([]);
  const [showDaireInfoModal, setShowDaireInfoModal] = useState(false);
  const [daireInfoData, setDaireInfoData] = useState('');
  const chatRef = useRef();

  const { FILE_NAMES } = require('../utils/constants');

  // Fetch Daire info data
  useEffect(() => {
    const fetchDaireInfo = async () => {
      try {
        const daireOptionsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.DAIRE_OPTIONS);
        const daireIntroContent = daireOptionsResult.fileContent.find(option => option.title === 'Disclaimer3');
        setDaireInfoData(daireIntroContent ? daireIntroContent.value : 'No content available');
      } catch (err) {
        console.error('Error fetching Daire info:', err);
        setDaireInfoData('No content available');
      }
    };

    fetchDaireInfo();
  }, []);

  // Handler to send question to chat
  const handleRelatedQuestionClick = (question) => {
    if (chatRef.current && chatRef.current.sendQuestionToChat) {
      chatRef.current.sendQuestionToChat(question);
      if (chatRef.current.scrollToBottom) {
        setTimeout(() => chatRef.current.scrollToBottom(), 100);
      }
    }
  };

  // Handler to send topic to chat
  const handleTopicClick = (topic) => {
    if (chatRef.current && chatRef.current.sendQuestionToChat) {
      chatRef.current.sendQuestionToChat(`Tell me about ${topic}`);
      if (chatRef.current.scrollToBottom) {
        setTimeout(() => chatRef.current.scrollToBottom(), 100);
      }
    }
  };

  const handlePinQuestion = (question) => {
    setPinnedQuestions((prev) => prev.includes(question) ? prev : [...prev, question]);
    if (chatRef.current && chatRef.current.sendQuestionToChat) {
      chatRef.current.sendQuestionToChat(question);
      if (chatRef.current.scrollToBottom) {
        setTimeout(() => chatRef.current.scrollToBottom(), 100);
      }
    }
  };

  const handleUnpinQuestion = (question) => {
    setPinnedQuestions((prev) => prev.filter(q => q !== question));
  };

  // Handler to trigger help from chat toolbar
  const handleShowHelp = () => setShowHelp(true);

  const renderContent = () => {
    switch (activeTab) {
      case 'documents':
        return <div className={styles.contentSection}>Documents Content</div>;
      case 'news':
        return <div className={styles.contentSection}>News Content</div>;
      case 'analytics':
        return <div className={styles.contentSection}>Analytics Content</div>;
      case 'history':
        return <div className={styles.contentSection}>Chat History</div>;
      case 'suggestions':
        return <div className={styles.contentSection}>Suggestions</div>;
      default:
        return null;
    }
  };

  // Only show sidebar if there is at least one user message
  const showSidebar = messages.some(m => m.type === 'user');

  return (
    <div className={styles.pageContainer}>
      {/* Main Content Area */}
      <div className={styles.mainContent}>
        {/* Left Navigation Sidebar */}
        <nav className={styles.sidebar} data-has-content={showSidebar}>
          <ul className={styles.navList}>
            {/* Only show sidebar content if there is at least one user message */}
            {showSidebar && (
              <>
                {/* Pinned Questions Section */}
                {pinnedQuestions.length > 0 && (
                  <li className={styles.pinnedQuestionsNav}>
                    <div className={styles.pinnedQuestionsHeader}>📌 Pinned Questions</div>
                    <ul className={styles.pinnedQuestionsList}>
                      {pinnedQuestions.map((q, i) => (
                        <li key={i} className={styles.pinnedQuestionItem}>
                          <span onClick={() => handleRelatedQuestionClick(q)} className={styles.pinnedQuestionText}>{q}</span>
                          <button className={styles.unpinButton} onClick={() => handleUnpinQuestion(q)} title="Unpin">
                            <FontAwesomeIcon icon={faThumbtack} />
                          </button>
                        </li>
                      ))}
                    </ul>
                  </li>
                )}
                {/* Related Questions */}
                <li className={styles.relatedQuestionsNav}>
                  {isUpdatingSummary ? (
                    <div className={styles.relatedQuestionsLoading}>
                      <span className={styles.spinner}></span>
                      Getting questions...
                    </div>
                  ) : relatedQuestions.length > 0 && (
                    <>
                      <div className={styles.relatedQuestionsHeader}>Related Questions</div>
                      <ul className={styles.relatedQuestionsList}>
                        {relatedQuestions.map((q, i) => (
                          <li key={i} className={styles.relatedQuestionItem}>
                            <span onClick={() => handleRelatedQuestionClick(q)} className={styles.relatedQuestionText}>{q}</span>
                            <button
                              className={styles.pinButton}
                              onClick={() => handlePinQuestion(q)}
                              title={pinnedQuestions.includes(q) ? 'Pinned' : 'Pin'}
                              disabled={pinnedQuestions.includes(q)}
                            >
                              <FontAwesomeIcon icon={faThumbtack} style={{ opacity: pinnedQuestions.includes(q) ? 1 : 0.5, transform: pinnedQuestions.includes(q) ? 'rotate(-20deg)' : 'none' }} />
                            </button>
                          </li>
                        ))}
                      </ul>
                    </>
                  )}
                </li>
              </>
            )}
          </ul>
        </nav>

        {/* Content Area */}
        <div className={styles.contentArea}>
          {activeTab === 'chat' ? (
            <div className="container">
              <div className={styles.daireContainer}>
                <div className={styles.daireIntro}>
                  <div className={styles.daireAvatar}>
                    <img src="/assets/img/daire/daire-avatar-nobg.png" alt="Daire Avatar" className={styles.backgroundImage} />
                    <button 
                      className="daire-info-btn" 
                      onClick={() => setShowDaireInfoModal(true)} 
                      aria-label="Daire Usage Policy"
                      title="Daire Usage Policy"
                    >
                      <i className="fa-solid fa-circle-info"></i>
                    </button>
                 </div>
                </div>
                <div className={styles.chatContainer}>
                  <DaireChat
                    ref={chatRef}
                    fullPage
                    onRelatedQuestionsUpdate={setRelatedQuestions}
                    onTopicsUpdate={setTopics}
                    onUpdatingSummaryChange={setIsUpdatingSummary}
                    onUpdatingTopicsChange={setIsUpdatingTopics}
                    onShowHelp={handleShowHelp}
                    onMessagesUpdate={setMessages}
                    isOnDairePage={true}
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className={styles.tabContent}>
              <div className={styles.tabHeader}>
                <h2>{activeTab.charAt(0).toUpperCase() + activeTab.slice(1)}</h2>
                <div className={styles.contentControls}>
                  <select 
                    value={contentType}
                    onChange={(e) => setContentType(e.target.value)}
                    className={styles.contentTypeSelect}
                  >
                    <option value="all">All Content</option>
                    <option value="recent">Recent</option>
                    <option value="favorites">Favorites</option>
                  </select>
                  <button 
                    className={styles.toggleContentButton}
                    onClick={() => setShowContent(!showContent)}
                  >
                    {showContent ? 'Hide Content' : 'Show Content'}
                  </button>
                </div>
              </div>
              {showContent && renderContent()}
            </div>
          )}
        </div>

        {/* Right Topics Sidebar */}
        <nav className={styles.topicsSidebar}>
          {isUpdatingTopics ? (
            <div className={styles.relatedQuestionsLoading}>
              <span className={styles.spinner}></span>
              Getting Topics...
            </div>
          ) : topics.length > 0 && (
            <div className={styles.topicsNav}>
              <div className={styles.topicsHeader}>Topics</div>
              <ul className={styles.topicsList}>
                {topics.map((topic, i) => (
                  <li key={i} className={styles.topicItem} onClick={() => handleTopicClick(topic)}>
                    {topic}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </nav>
      </div>

      {/* Help Modal */}
      {showHelp && (
        <div className={styles.helpModal}>
          <div className={styles.helpContent}>
            <h3>Daire AI Assistant Help</h3>
            <p>Welcome to Daire AI Assistant! Here are some tips to get started:</p>
            <ul>
              <li>Use the chat interface to ask questions and get answers</li>
              <li>View your chat history and analytics</li>
              <li>Get personalized suggestions based on your interests</li>
            </ul>
            <button 
              className={styles.closeHelpButton}
              onClick={() => setShowHelp(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* Daire Info Modal */}
      {showDaireInfoModal && (
        <InfoModal
          title="Daire Usage Policy"
          data={daireInfoData}
          show={showDaireInfoModal}
          onClose={() => setShowDaireInfoModal(false)}
          small={true}
        />
      )}
    </div>
  );
}

export default DairePage;